import { useState } from 'react'
import { Box, Text } from '@mantine/core'
import { useAtomValue } from 'jotai'
import { SectionLabel } from './SectionLabel'
import { LipSyncPortrait } from '../common/LipSyncPortrait'
import { SpeechVisualizer } from '../common/speechViz/SpeechVisualizer'
import { DEFAULT_VIZ_STYLE } from '../common/speechViz/registry'
import {
  activeSpeakerIdAtom,
  activeSpeakerNameAtom,
  activeSpeakingAtom,
} from '../../atoms/speechAtoms'

// Scene art, smaller but permanently visible (no longer a full-bleed backdrop),
// so gaining reading area never costs the art. Tap to enlarge to a full overlay.
//
// While a VOICE is speaking (a character OR the narrator), the speaker's name
// shows beneath the portrait and a speech visualizer (driven by the viseme
// stream) animates in the scope frame below it. A character's large portrait
// cross-fades in over the scene; the narrator has no portrait, so the scene art
// stays put and only the name + visualizer indicate the narrator is talking. The
// scope frame keeps a FIXED reserved height at all times so the layout never
// jumps as portraits come and go.
//
// TODO: vizStyle is hard-coded to the default for now; this becomes a
// per-story / per-character author setting (the registry key is what persists).
export function SceneWindow({ sceneUrl, onEnlarge, characters = [], vizStyle = DEFAULT_VIZ_STYLE }) {
  const activeSpeakerId = useAtomValue(activeSpeakerIdAtom)
  const speaking = useAtomValue(activeSpeakingAtom)
  const speakerName = useAtomValue(activeSpeakerNameAtom)

  // The character with a portrait to show (null for the narrator, who has none).
  const speaker = activeSpeakerId
    ? characters.find((c) => c.id === activeSpeakerId)
    : null

  // Remember the last portrait-bearing speaker so it can fade OUT (rather than
  // vanishing). Uses React's "adjust state during render" pattern (no effect).
  const [lastSpeaker, setLastSpeaker] = useState(null)
  if (speaker && speaker !== lastSpeaker) setLastSpeaker(speaker)
  const portraitCharacter = speaker || lastSpeaker || characters[0] || null

  // The visualizer needs a character only to satisfy the per-character envelope
  // path; in hero mode it animates on ANY voice (anyVoice), so the narrator
  // drives it too. Reuse a portrait-bearing character as a stand-in identity.
  const vizCharacter = portraitCharacter
  const hasVoices = vizCharacter || speaking

  return (
    <Box p="md" style={{ borderBottom: '1px solid var(--bw-panel-border)' }}>
      <SectionLabel>{speaking && speakerName ? speakerName : 'Scene'}</SectionLabel>
      <Box
        onClick={sceneUrl && !speaker ? onEnlarge : undefined}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 10',
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid var(--bw-panel-border)',
          background: sceneUrl
            ? `center / cover no-repeat url(${sceneUrl})`
            : 'color-mix(in srgb, var(--bw-body-color) 8%, transparent)',
          cursor: sceneUrl && !speaker ? 'zoom-in' : 'default',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
        }}
      >
        {/* Speaking-character portrait overlay: cross-fades over the scene. Only
            shows for a character (the narrator has no portrait); the last
            character stays mounted through the fade-out so it eases away. */}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            opacity: speaker ? 1 : 0,
            transition: 'opacity 320ms ease',
            pointerEvents: 'none',
          }}
        >
          {portraitCharacter && <LipSyncPortrait character={portraitCharacter} fill />}
        </Box>

        {sceneUrl && !speaker && (
          <Text size="xs" px={6} py={2} m={6} style={{ background: 'rgba(0,0,0,0.5)', borderRadius: 4, color: '#fff', pointerEvents: 'none' }}>
            tap to enlarge ⤢
          </Text>
        )}
        {!sceneUrl && !speaker && (
          <Box style={{ margin: 'auto' }}>
            <Text size="xs" c="dimmed">No scene art</Text>
          </Box>
        )}
      </Box>

      {/* Speaker name + speech visualizer. The name (character OR "Narrator")
          shows whenever a voice is playing; the visualizer keeps a FIXED
          reserved height at all times so the layout never shifts, animating for
          any voice and sitting at an idle shimmer otherwise. Only rendered when
          the story has voiced content at all. */}
      {hasVoices && (
        <Box mt={8}>
          <Box
            style={{
              height: 18,
              marginBottom: 4,
              textAlign: 'center',
              opacity: speaking && speakerName ? 1 : 0,
              transition: 'opacity 200ms ease',
            }}
          >
            <Text
              size="sm"
              fw={700}
              ff="var(--bw-heading-font)"
              style={{ color: 'var(--bw-accent)', letterSpacing: '0.04em', lineHeight: '18px' }}
            >
              {speakerName || ' '}
            </Text>
          </Box>
          <SpeechVisualizer character={vizCharacter} styleKey={vizStyle} height={56} />
        </Box>
      )}
    </Box>
  )
}
